import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { useAtom, useAtomValue } from 'jotai';

import { DirectMessageService } from '@/components/pages/conversations/SocialInbox/services/DirectMessageService';

import { useSocialInboxDataSource } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxDataSource';
import { useDMConversationMessages } from '@/utils/hooks/swr/conversations/useDMConversationMessages';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useAcl from '@/utils/hooks/user/useAcl';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { classNames } from '@/utils';
import { mixPanelDMMessageSent } from '@/utils/mixpanel/directMessageEvents';

import {
    DMConversation,
    DMConversationMessage
} from '@/components/pages/conversations/SocialInbox/types';

import { aclFeatures } from '@/lib/acl/aclFeatures';

import {
    openedMessageAtom,
    replyMessageAtom
} from '@/components/pages/conversations/SocialInbox/atoms';

import { CommentForm } from '@/components/composite/Comments/CommentForm';
import { DirectMessagesInputReplyCard } from '@/components/pages/conversations/SocialInbox/components/SocialInboxChatView/SocialInboxMessages/SocialInboxDirectMessages/DirectMessageInput/DirectMessageInputReplyCard';

export const DirectMessageInput = ({
    onSend,
    disabled = false
}: {
    onSend: () => void;
    disabled?: boolean;
}) => {
    const { t } = useTranslation('conversations');
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const [replyMessage] = useAtom(replyMessageAtom);
    const openedMessage = useAtomValue(openedMessageAtom) as DMConversation;

    const canCreateDirectMessages = useAcl().hasAcl(aclFeatures.direct_messages.base.create);
    const { resetField } = useFormContext();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { mutateMessages: mutateConversations } = useSocialInboxDataSource();
    const { mutate: mutateMessages } = useDMConversationMessages({
        activeSPID: activeSocialProfile?.id,
        conversationId: openedMessage.meta_conversation_id
    });

    const updateMessageStatus = (message: DMConversationMessage) => {
        mutateMessages((data) => DirectMessageService.hydrateMessages(data, message), {
            revalidate: false
        });
        mutateConversations(
            (conversations: DMConversation[]) =>
                DirectMessageService.hydrateConversations(conversations, message, true),
            { revalidate: false }
        );
    };

    const handleSubmitMethod = (method: 'enter_key' | 'send_button') => {
        mixPanelDMMessageSent(method);
    };

    const handleSubmit = async ({ message }: { message: string }) => {
        onSend();

        await DirectMessageService.sendMessage(
            message,
            activeSocialProfile,
            openedMessage.meta_conversation_id,
            updateMessageStatus
        );

        resetField('message');

        // Auto-focus the input after sending
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    return (
        <div data-testid="direct-message-input" className="flex flex-col p-4 pt-0 sm:p-10 sm:pt-0">
            {replyMessage && <DirectMessagesInputReplyCard />}

            <CommentForm
                comments={[]}
                className={classNames({ 'rounded-t-none': !!replyMessage })}
                variant="socialInbox"
                placeholder={t('direct_messages.input.placeholder')}
                submitAction={handleSubmit}
                disabled={!canCreateDirectMessages || disabled}
                enableEnterSubmit
                onSubmitMethod={handleSubmitMethod}
                inputRef={inputRef}
            />
        </div>
    );
};
